import { Controller } from "@hotwired/stimulus"
import { api } from "../composables/useApi"

export default class extends Controller {
  static targets = ["themeTile", "themeCurrent", "segment"]
  static values = { userId: Number }

  // === Toggle switches (high_contrast, animation_disabled, skip_previous_animations) ===

  toggleSwitch(event) {
    const button = event.currentTarget
    const pref = button.dataset.pref
    if (!pref) return
    const wasOn = button.getAttribute("aria-checked") === "true"
    const nextOn = !wasOn
    this.#applySwitch(button, nextOn)
    if (pref === "high_contrast") {
      document.body.classList.toggle("high-contrast", nextOn)
    }
    this.#updatePreference(pref, nextOn, () => {
      this.#applySwitch(button, wasOn)
      if (pref === "high_contrast") {
        document.body.classList.toggle("high-contrast", wasOn)
      }
    })
  }

  toggleKeydown(event) {
    if (event.key === " " || event.key === "Enter") {
      event.preventDefault()
      this.toggleSwitch(event)
    }
  }

  #applySwitch(button, on) {
    button.classList.toggle("is-on", on)
    button.setAttribute("aria-checked", String(on))
  }

  // === Text-size segmented control ===

  selectSegment(event) {
    const target = event.currentTarget
    const value = target.dataset.value
    if (!value) return
    const previous = this.#findBodyClass(/^text-/)
    this.#applySegment(target)
    this.#applyBodyClass(/^text-/, `text-${value}`)
    this.#updatePreference("text_size", value, () => {
      const previousTarget = this.segmentTargets.find(s => previous && `text-${s.dataset.value}` === previous)
      if (previousTarget) this.#applySegment(previousTarget)
      if (previous) this.#applyBodyClass(/^text-/, previous)
    })
  }

  segmentKeydown(event) {
    if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return
    event.preventDefault()
    const current = event.currentTarget
    const idx = this.segmentTargets.indexOf(current)
    if (idx === -1) return
    const delta = event.key === "ArrowRight" ? 1 : -1
    const next = this.segmentTargets[(idx + delta + this.segmentTargets.length) % this.segmentTargets.length]
    next.focus()
    next.click()
  }

  #applySegment(target) {
    this.segmentTargets.forEach(s => {
      const active = s === target
      s.classList.toggle("is-active", active)
      s.setAttribute("aria-checked", String(active))
    })
  }

  // === Theme swatch picker ===

  selectThemeTile(event) {
    const tile = event.currentTarget
    if (tile.getAttribute("aria-disabled") === "true" || tile.disabled) return
    const value = tile.dataset.theme
    const name = tile.dataset.themeName || value
    if (!value) return
    const previous = this.#findBodyClass(/^theme-/)
    this.#applyThemeTile(tile, name)
    this.#applyBodyClass(/^theme-/, `theme-${value}`)
    this.#updatePreference("theme", value, () => {
      if (!previous) return
      const previousId = previous.replace(/^theme-/, "")
      const previousTile = this.themeTileTargets.find(t => t.dataset.theme === previousId)
      if (previousTile) this.#applyThemeTile(previousTile, previousTile.dataset.themeName || previousId)
      this.#applyBodyClass(/^theme-/, previous)
    })
  }

  themeKeydown(event) {
    if (event.key !== "ArrowLeft" && event.key !== "ArrowRight" &&
        event.key !== "ArrowUp" && event.key !== "ArrowDown") return
    event.preventDefault()
    const enabled = this.themeTileTargets.filter(t => t.getAttribute("aria-disabled") !== "true" && !t.disabled)
    if (!enabled.length) return
    const current = event.currentTarget
    const idx = enabled.indexOf(current)
    if (idx === -1) return
    const delta = (event.key === "ArrowRight" || event.key === "ArrowDown") ? 1 : -1
    const next = enabled[(idx + delta + enabled.length) % enabled.length]
    next.focus()
    next.click()
  }

  #applyThemeTile(tile, name) {
    this.themeTileTargets.forEach(t => {
      const active = t === tile
      t.classList.toggle("is-active", active)
      if (t.getAttribute("aria-disabled") !== "true") {
        t.setAttribute("aria-checked", String(active))
      }
    })
    if (this.hasThemeCurrentTarget) this.themeCurrentTarget.textContent = name
  }

  // === Body-class helpers (text size + theme) ===

  #findBodyClass(pattern) {
    return [...document.body.classList].find(cls => pattern.test(cls))
  }

  #applyBodyClass(pattern, nextClass) {
    document.body.classList.forEach(cls => {
      if (pattern.test(cls)) document.body.classList.remove(cls)
    })
    document.body.classList.add(nextClass)
  }

  // === Persistence ===

  async #updatePreference(field, value, revert) {
    try {
      await api.patch(`/users/${this.userIdValue}`, { user: { [field]: value } })
    } catch {
      revert?.()
    }
  }
}
